import { ClipStatus } from './ClipStatus';
import { ClipType } from './ClipType';

/**
 * Camera selection for a clip export: a gameCamera id, or the Auto-Director
 * sentinel for a clip the player cuts between every camera.
 */
export const AUTO_DIRECTOR_CAMERA = 'auto-director' as const;
export type ClipCameraSelection = number | typeof AUTO_DIRECTOR_CAMERA;

/** What an auto-director clip shows wherever a camera name would go. */
export const AUTO_DIRECTOR_CAMERA_LABEL = 'Auto-Director';

/**
 * Shown wherever Auto-Director is offered on a game with `enableTracking`
 * off. The director cuts between cameras by following the tracked object, so
 * with tracking off it has nothing to follow — both the interactive player
 * and clip export lose it together.
 */
export const AUTO_DIRECTOR_TRACKING_DISABLED_TOOLTIP =
  'Auto-Director is unavailable because tracking is disabled for this game';

// Base interface with common properties
export interface BaseClipModel {
  id: number | string;
  objectTrackingId: number;
  cameraId: number;
  cameraMode?: 'tracked' | 'fixed';
  // True for clips cut by the player's auto-director; cameraId is null for those.
  autoDirector?: boolean;
  description: string;
  clouduser: any;
  createdAt: Date | string;
  note: string;
  startTimestamp?: Date | string;
  endTimestamp?: Date | string;
  status: ClipStatus | string;
  pan?: number;
  tilt?: number;
  zoom?: number;
  /** The framing preset a fixed-camera clip was framed with (C360-3321). */
  framingPresetId?: number | null;
  clipgroupId?: number;
  type?: any;
}

// ClipModel extends base model with additional properties
export interface ClipModel extends BaseClipModel {
  downloadUrl?: string;
  endTimestamp?: Date | string;
}

/**
 * Clip as the clip endpoints return it: a ClipModel whose timestamps may still
 * be ISO strings and whose type arrives as `mediatype`. toClipModel normalises
 * both. Distinct from the list endpoint's shape in hooks/useAllClipsQuery.
 */
export type ClipModelFromServer = ClipModel & { mediatype?: ClipType };
